import LoginPage from './pages/Login';
import './App.css';
import { Link, Route, Routes, BrowserRouter, useLocation } from 'react-router-dom';
import Register from './pages/Register';
import ResetPassword from './pages/ResetPassword';
import NavBar from './components/NavBar/NavBar';
import Banner from './components/Banner/Banner';
import { AuthProvider, useAuth } from './context/AuthContext';
import Admin from './pages/Admin';
import ModuleCreationPage from './pages/ModuleCreationPage';
import ModuleBuilderPage from './pages/ModuleBuilderPage';
import { RoleGuard } from './components/Shared/RoleGuard';
import MyAssignments from './pages/MyAssignments';
import AssignmentExecutionPage from './pages/AssignmentExecutionPage';
import Questions from './components/Questions';
import { UserPolicies } from './models/user-policy';
import { UserRole } from './models/UserRole';
import homeStyles from './pages/Home/Home.module.css';
import { ROUTES } from './constants/contstants';
import AiChatDemo from './pages/AiChat';
import DemoExam from './components/DemoExam/DemoExam';
import ContactUs from './pages/ContactUs/ContactUs';
import Onboarding from './pages/Onboarding';

function HomePage() {
  const { isAuthenticated, userRoles } = useAuth();
  const isExamTaker = userRoles.includes(UserRole.EXAM_TAKER);
  const canAccessBackoffice = isAuthenticated && (
    UserPolicies.hasContributorAccess(userRoles) ||
    UserPolicies.hasAnalystAccess(userRoles)
  );
  const showPublicAccessActions = !isAuthenticated && !canAccessBackoffice;
  const canOpenAssignments = isAuthenticated && isExamTaker;

  return (
    <div className={homeStyles.homePage}>
      <div className={homeStyles.container}>
        <div className={homeStyles.hero}>
          <h1 className={homeStyles.title}>
            Welcome to <span className={homeStyles.brandName}>247Labs Academy</span>
          </h1>
          <p className={homeStyles.subtitle}>
            Learn, practise, and demonstrate the skills that help you grow at BetPikr.
            Complete curated assessments built for every Academy learner, across every team.
          </p>

          <div className={homeStyles.features}>
            <div className={homeStyles.feature}>
              <div className={homeStyles.featureIcon}><img src="/images/icons/notepad.svg" alt="Create" style={{width: '48px', height: '48px'}} /></div>
              <h3 className={homeStyles.featureTitle}>Learn with purpose</h3>
              <p className={homeStyles.featureDesc}>Build practical knowledge through focused learning and assessments</p>
            </div>
            <div className={homeStyles.feature}>
              <div className={homeStyles.featureIcon}><img src="/images/icons/chart.svg" alt="Track" style={{width: '48px', height: '48px'}} /></div>
              <h3 className={homeStyles.featureTitle}>Track your progress</h3>
              <p className={homeStyles.featureDesc}>See your development clearly as you move through the programme</p>
            </div>
            <div className={homeStyles.feature}>
              <div className={homeStyles.featureIcon}><img src="/images/icons/rocket.svg" alt="Deploy" style={{width: '48px', height: '48px'}} /></div>
              <h3 className={homeStyles.featureTitle}>Grow at BetPikr</h3>
              <p className={homeStyles.featureDesc}>Turn what you learn into stronger work across the business</p>
            </div>
          </div>

          {canOpenAssignments && (
            <Link
              to={ROUTES.MY_ASSIGNMENTS}
              className={homeStyles.examCTA}
            >
              <div className={homeStyles.examCTAContent}>
                <h2 className={homeStyles.examCTATitle}>Ready for your next Academy assessment?</h2>
                <p className={homeStyles.examCTADescription}>
                  Your assigned learning and assessments are waiting in your personalised dashboard.
                </p>
                <div className={homeStyles.examCTAHint}>Open my assignments →</div>
              </div>
            </Link>
          )}

          <div className={homeStyles.actions}>
            {showPublicAccessActions && (
              <>
                <Link
                  to={ROUTES.LOGIN}
                  className={homeStyles.primaryButton}
                >
                  Sign in
                </Link>
                <Link
                  to={ROUTES.ONBOARDING}
                  className={homeStyles.secondaryButton}
                >
                  Enter invite code
                </Link>
              </>
            )}
            {canOpenAssignments && (
              <Link
                to={ROUTES.MY_ASSIGNMENTS}
                className={homeStyles.primaryButton}
              >
                My assignments
              </Link>
            )}
            {canAccessBackoffice && (
              <Link
                to={ROUTES.ADMIN}
                className={homeStyles.primaryButton}
              >
                Open Academy backoffice
              </Link>
            )}
          </div>

          <div className={homeStyles.footer}>
            <p className={homeStyles.footerText}>
              247Labs Academy · BetPikr Graduate Programme
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function AppContent() {
  const location = useLocation();
  const isExamPage = location.pathname.startsWith('/assignment/') || 
                     location.pathname.startsWith(ROUTES.QUESTIONS);

  return (
    <>
      {!isExamPage && <NavBar />}
      {!isExamPage && <Banner />}
      <Routes>
        <Route path={ROUTES.HOME} element={<HomePage />} />
        <Route path={ROUTES.REGISTER} element={<Register />} />
        <Route path={ROUTES.ONBOARDING} element={<Onboarding />} />
        <Route path={ROUTES.RESET_PASSWORD} element={<ResetPassword />} />
        <Route path={ROUTES.ADMIN} element={
          <RoleGuard requiredRoles={[...UserPolicies.Contributors, ...UserPolicies.Analysts]} redirectTo={ROUTES.LOGIN}>
            <Admin />
          </RoleGuard>
        } />
        <Route path={ROUTES.LOGIN} element={<LoginPage />} />
        <Route path={ROUTES.MY_ASSIGNMENTS} element={<MyAssignments />} />
        <Route path={ROUTES.ASSIGNMENT} element={<AssignmentExecutionPage />} />
        <Route path={ROUTES.QUESTIONS} element={<Questions />} />
        <Route path={ROUTES.MODULE_CREATE} element={
          <RoleGuard requiredRoles={[...UserPolicies.Contributors]} redirectTo={ROUTES.LOGIN}>
            <ModuleCreationPage />
          </RoleGuard>
        } />
        <Route path={ROUTES.MODULE_BUILD} element={
          <RoleGuard requiredRoles={[...UserPolicies.Contributors]} redirectTo={ROUTES.LOGIN}>
            <ModuleBuilderPage />
          </RoleGuard>
        } />
        <Route path={ROUTES.AI_CHAT} element={
          <RoleGuard requiredRoles={[...UserPolicies.Contributors]} redirectTo={ROUTES.LOGIN}>
            <AiChatDemo />
          </RoleGuard>
        } />
        <Route path={ROUTES.DEMO} element={<DemoExam />} />
        <Route path={ROUTES.CONTACT_US} element={<ContactUs />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
